/* The visual policy builder's presentation model: draft rows, their validity, and what they mean.
 *
 * **Two jobs, and the second is the one a form does not get for free.** The first is bookkeeping —
 * add a row, edit a field, remove a row, and turn the rows into the document the API takes. The
 * second is answering "what does this policy actually do", which is a question a list of rows does
 * not answer on its own: deny wins, so a broad allow read literally overstates what it grants, and
 * a specific allow under a broad deny grants nothing at all while looking like it grants something.
 * `decisionsFor` is that reading, and it lives here so it can be tested.
 *
 * The recurring rule, as everywhere else in this feature: **an empty list of problems is not a
 * valid policy.** A failed validation request produces the same empty list as a sound one, so every
 * reading answers from an explicit field.
 */

import type {
  PolicyDocument,
  PolicyProblem,
  PolicyRule,
  PolicyValidation,
  PolicyVocabulary,
} from '@/shared/api/generated/policy';
import type { AlertTone } from '@/shared/ui/Alert';

/** A row being edited. **Every field is optional, unlike `PolicyRule`.**
 *
 * A half-filled row is the normal state of a form somebody is using, and modelling it with the
 * wire type would force a placeholder — an empty-string role, or `effect: 'allow'` chosen by the
 * code rather than the person. Both are worse than absence: a placeholder role would be sent to the
 * server as a real value, and a defaulted effect would mean the most consequential field on the row
 * was filled in by nobody.
 *
 * `id` is local and never sent. It exists so React can key a row that has no distinguishing content
 * yet — two blank rows are otherwise identical, and keying by index makes a removal re-render the
 * wrong row's contents.
 */
export interface DraftRule {
  readonly id: string;
  readonly role: string | null;
  readonly resourceType: string | null;
  readonly action: string | null;
  readonly resourceId: string | null;
  readonly effect: 'allow' | 'deny' | null;
}

/** `resourceId` is deliberately not in this list.
 *
 * Null there is a *choice* — "every resource of this type" — rather than an unanswered question, so
 * a row without one is complete. That is the one field where absence carries meaning, which is why
 * completeness cannot be a simple "no nulls" check.
 */
const REQUIRED: readonly (keyof DraftRule)[] = [
  'role',
  'resourceType',
  'action',
  'effect',
];

let counter = 0;

/** A blank row. Sequential ids rather than random ones so a test can assert on them. */
export function blankRule(): DraftRule {
  counter += 1;
  return {
    id: `rule-${String(counter)}`,
    role: null,
    resourceType: null,
    action: null,
    resourceId: null,
    effect: null,
  };
}

/** Reopens saved rules for editing. */
export function draftFrom(rules: readonly PolicyRule[]): readonly DraftRule[] {
  return rules.map((rule) => {
    counter += 1;
    return {
      id: `rule-${String(counter)}`,
      role: rule.role,
      resourceType: rule.resource_type,
      action: rule.action,
      resourceId: rule.resource_id,
      effect: rule.effect === 'deny' ? ('deny' as const) : ('allow' as const),
    };
  });
}

/** Whether a row has everything the API needs. */
export function isComplete(rule: DraftRule): boolean {
  return REQUIRED.every((field) => {
    const value = rule[field];
    return typeof value === 'string' && value.length > 0;
  });
}

/** The rows that are ready, as the document the API takes. Incomplete rows are left out.
 *
 * **Silently dropping an incomplete row would be wrong**, which is why `canSubmit` refuses while
 * any row is incomplete rather than letting this quietly send fewer rules than the form shows. This
 * function is the translation; the refusal is next door.
 */
export function documentFrom(rules: readonly DraftRule[]): PolicyDocument {
  return {
    rules: rules.filter(isComplete).map((rule) => ({
      role: rule.role ?? '',
      action: rule.action ?? '',
      resource_type: rule.resourceType ?? '',
      resource_id: rule.resourceId,
      effect: rule.effect === 'deny' ? 'deny' : 'allow',
    })),
  };
}

/** Which actions a resource type has, or none when the type is unknown or unchosen.
 *
 * Read from the served vocabulary rather than a local table, so the Action dropdown cannot offer
 * something no request carries — a rule naming one would compile and match nothing.
 */
export function actionsFor(
  vocabulary: PolicyVocabulary | undefined,
  resourceType: string | null,
): readonly string[] {
  if (vocabulary === undefined || resourceType === null) return [];
  return (
    vocabulary.resources.find((each) => each.resource_type === resourceType)?.actions ??
    []
  );
}

/** Applies an edit, clearing what the edit invalidates.
 *
 * **Changing the resource type clears the action**, and that is the one piece of behaviour here
 * worth defending. Actions are per resource type — `catalog.dataset` has `read` and `profile`,
 * `rules.rule` has others — so a row left holding `profile` after a switch to `rules.rule` would be
 * a rule the server refuses, and worse, one the form shows as filled in. Clearing the resource id
 * too, because an id belongs to a type.
 */
export function withEdit(
  rule: DraftRule,
  field: 'role' | 'resourceType' | 'action' | 'resourceId' | 'effect',
  value: string | null,
): DraftRule {
  const chosen = value === null || value === '' ? null : value;
  if (field === 'resourceType') {
    return { ...rule, resourceType: chosen, action: null, resourceId: null };
  }
  if (field === 'effect') {
    return {
      ...rule,
      effect: chosen === 'deny' ? 'deny' : chosen === 'allow' ? 'allow' : null,
    };
  }
  return { ...rule, [field]: chosen };
}

/** Whether the policy can be sent. */
export function canSubmit(
  rules: readonly DraftRule[],
  vocabulary: PolicyVocabulary | undefined,
  busy: boolean,
): boolean {
  if (!vocabulary?.compiler_available) return false;
  if (busy) return false;
  if (rules.length === 0) return false;
  if (rules.length > vocabulary.max_rules) return false;
  /* Every row, not just some. An incomplete row means the form shows a rule the server would never
   * receive, and sending the rest would save a policy that is not the one on screen. */
  return rules.every(isComplete);
}

/** Why the policy cannot be sent, as a copy key — or null when it can. */
export function blockedReasonKey(
  rules: readonly DraftRule[],
  vocabulary: PolicyVocabulary | undefined,
): string | null {
  if (vocabulary === undefined) return 'policy.build.blocked.loading';
  if (!vocabulary.compiler_available) return 'policy.build.blocked.noCompiler';
  if (vocabulary.roles.length === 0) return 'policy.build.blocked.noRoles';
  if (rules.length === 0) return 'policy.build.blocked.empty';
  if (rules.length > vocabulary.max_rules) return 'policy.build.blocked.tooMany';
  if (!rules.every(isComplete)) return 'policy.build.blocked.incomplete';
  return null;
}

/** How a validation attempt reads. */
export type ValidationReading =
  'unchecked' | 'checking' | 'valid' | 'problems' | 'failed';

export interface ValidationState {
  readonly reading: ValidationReading;
  readonly tone: AlertTone | 'neutral';
  readonly count: number;
}

/** Reads a validation result. **`valid` comes from the server's own field, never from a count.**
 *
 * A failed request, a response that did not parse, and a sound policy all produce an empty problems
 * list, and only one of them is valid. `unchecked` is deliberately not `valid` either: rules nobody
 * has checked are not rules known to be sound, and the save is the next click.
 */
export function validationReading(
  result: PolicyValidation | undefined,
  pending: boolean,
  failed: boolean,
): ValidationState {
  if (pending) return { reading: 'checking', tone: 'neutral', count: 0 };
  if (failed) return { reading: 'failed', tone: 'error', count: 0 };
  if (result === undefined) return { reading: 'unchecked', tone: 'neutral', count: 0 };
  if (result.valid) return { reading: 'valid', tone: 'ok', count: 0 };
  return { reading: 'problems', tone: 'warning', count: result.problems.length };
}

/** How a save attempt reads. */
export type SaveReading = 'idle' | 'saving' | 'stored' | 'refused' | 'failed';

export interface SaveState {
  readonly reading: SaveReading;
  readonly tone: AlertTone | 'neutral';
  readonly problems: readonly PolicyProblem[];
}

/** Reads a save attempt. **`stored` is the field; the problems list is not the signal.**
 *
 * The platform also refuses a policy for reasons that carry no per-rule attribution — a document
 * that is not a policy at all, a generator fault — and those arrive with an empty list. Branching
 * on the list would render one of those as a saved draft.
 */
export function saveReading(
  result:
    { readonly stored: boolean; readonly problems: readonly PolicyProblem[] } | undefined,
  pending: boolean,
  failed: boolean,
): SaveState {
  if (pending) return { reading: 'saving', tone: 'neutral', problems: [] };
  if (failed) return { reading: 'failed', tone: 'error', problems: [] };
  if (result === undefined) return { reading: 'idle', tone: 'neutral', problems: [] };
  if (result.stored) return { reading: 'stored', tone: 'ok', problems: [] };
  return { reading: 'refused', tone: 'warning', problems: result.problems };
}

/** Problems that belong to one row, by its 1-based position. */
export function problemsForRule(
  problems: readonly PolicyProblem[],
  index: number,
): readonly PolicyProblem[] {
  return problems.filter((each) => each.rule_number === index + 1);
}

/** Problems that belong to no row — the policy as a whole, or the platform's own generator. */
export function policyLevelProblems(
  problems: readonly PolicyProblem[],
): readonly PolicyProblem[] {
  return problems.filter((each) => each.rule_number === null);
}

/** What a rule actually decides, once deny-overrides is taken into account. */
export type DecisionNote = 'plain' | 'narrowed' | 'shadowed';

export interface DecisionLine {
  readonly id: string;
  readonly role: string;
  readonly action: string;
  readonly resourceType: string;
  readonly resourceId: string | null;
  readonly effect: 'allow' | 'deny';
  readonly note: DecisionNote;
  /** 1-based positions of the rules that narrow or shadow this one. */
  readonly by: readonly number[];
}

/** The resulting access decisions, which a list of rows does not state on its own.
 *
 * **Deny wins, so two of the three readings exist because a row read literally is misleading.**
 *
 * * `narrowed` — a broad allow (every resource of its type) with one or more specific denies over
 *   it. "Stewards may read datasets" is true except where it is not, and the exception is the
 *   interesting half.
 * * `shadowed` — a specific allow underneath a broad deny for the same role and action. It grants
 *   **nothing**, and the form shows it as an allow. Not invalid, so the server accepts it; almost
 *   certainly not what the author meant, so the screen says so.
 * * `plain` — neither.
 *
 * A contradiction (identical target, opposite effects) cannot appear here: the server refuses it,
 * so a policy that reaches this function has at most one effect per target.
 */
export function decisionsFor(rules: readonly DraftRule[]): readonly DecisionLine[] {
  const complete = rules
    .map((rule, index) => ({ rule, position: index + 1 }))
    .filter((each) => isComplete(each.rule));

  return complete.map(({ rule, position }) => {
    const role = rule.role ?? '';
    const action = rule.action ?? '';
    const resourceType = rule.resourceType ?? '';
    const effect = rule.effect === 'deny' ? ('deny' as const) : ('allow' as const);

    const sameTarget = complete.filter(
      (other) =>
        other.position !== position &&
        other.rule.role === role &&
        other.rule.action === action &&
        other.rule.resourceType === resourceType,
    );

    if (effect === 'allow' && rule.resourceId === null) {
      const denies = sameTarget.filter(
        (other) => other.rule.effect === 'deny' && other.rule.resourceId !== null,
      );
      if (denies.length > 0) {
        return {
          ...line(rule, position, effect),
          note: 'narrowed' as const,
          by: denies.map((each) => each.position),
        };
      }
    }

    if (effect === 'allow' && rule.resourceId !== null) {
      const broadDenies = sameTarget.filter(
        (other) => other.rule.effect === 'deny' && other.rule.resourceId === null,
      );
      if (broadDenies.length > 0) {
        return {
          ...line(rule, position, effect),
          note: 'shadowed' as const,
          by: broadDenies.map((each) => each.position),
        };
      }
    }

    return { ...line(rule, position, effect), note: 'plain' as const, by: [] };
  });
}

function line(
  rule: DraftRule,
  position: number,
  effect: 'allow' | 'deny',
): Omit<DecisionLine, 'note' | 'by'> {
  return {
    id: `${rule.id}-${String(position)}`,
    role: rule.role ?? '',
    action: rule.action ?? '',
    resourceType: rule.resourceType ?? '',
    resourceId: rule.resourceId,
    effect,
  };
}

/** Whether the policy, as built, would permit nothing at all.
 *
 * The generated module is `allow if { permitted; not denied }` over `default permitted := false`,
 * so a policy carrying only deny rules never reaches a permit: it refuses **every** request in the
 * tenant rather than only the ones its rules name. Activating one is indistinguishable, from the
 * outside, from activating an empty bundle -- which is the state `cannotRemoveLast` already refuses
 * by the other route.
 *
 * It is invisible on the form, which is why it is worth a sentence. Each row is individually valid,
 * the server's reference and contradiction checks both pass, and the summary explains
 * deny-overrides in general rather than saying what *these* rows do.
 *
 * Judged over the complete rules only. A form part way through its first allow rule is not yet an
 * assertion about anything, and accusing its author of building a total denial would be the
 * cry-wolf failure that gets a warning switched off rather than read.
 */
export function permitsNothing(rules: readonly DraftRule[]): boolean {
  const complete = rules.filter(isComplete);
  return complete.length > 0 && complete.every((rule) => rule.effect === 'deny');
}

/** Whether a policy built here would actually be consulted once activated.
 *
 * Every step of the builder succeeds on a deployment whose evaluator is unwired, and the policy then
 * decides nothing. The screen says so before the work rather than after it — the same argument the
 * enforcement banner makes one panel up.
 */
export function builtPolicyWouldDecide(
  state:
    { readonly runtime_available: boolean; readonly abac_enabled: boolean } | undefined,
): boolean {
  if (state === undefined) return false;
  return state.runtime_available && state.abac_enabled;
}
